import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { settings as settingsApi, bankAccounts as bankAccountsApi, zoom as zoomApi, uploads as uploadsApi } from '@/utils/api';
import { Rocket, Zap, Send, CreditCard, Video, UploadCloud, ArrowRight, X, Check } from 'lucide-react';

const DISMISS_KEY = 'krris_setup_dismissed_v1';

export default function SetupChecklist() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const [state, setState] = useState({
    claude: false, smtp: false, bank: false, zoom: false, r2: false,
  });

  useEffect(() => {
    if (dismissed) { setLoading(false); return; }
    Promise.allSettled([
      settingsApi.get(),
      bankAccountsApi.list(),
      zoomApi.getConfig(),
      uploadsApi.getConfig(),
    ]).then(([s, b, z, u]) => {
      const set = s.status === 'fulfilled' ? s.value.data : {};
      const banks = b.status === 'fulfilled' ? b.value.data : [];
      const zc = z.status === 'fulfilled' ? z.value.data : {};
      const uc = u.status === 'fulfilled' ? u.value.data : {};
      setState({
        claude: !!set.claude_api_key_set,
        smtp: !!set.smtp_pass_set,
        bank: Array.isArray(banks) && banks.length > 0,
        zoom: !!(zc.enabled && zc.account_id && zc.client_id_set && zc.client_secret_set),
        r2: !!(uc.enabled && uc.account_id && uc.bucket && uc.access_key_set && uc.secret_key_set && uc.public_base_url),
      });
    }).finally(() => setLoading(false));
  }, [dismissed]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch {}
    setDismissed(true);
  };

  if (dismissed || loading) return null;

  const steps = [
    { key: 'claude', icon: Zap,        label: 'Connect Claude',    hint: 'Power AI briefs + estimates', to: '/admin/settings/integrations',  color: '#006080', required: true },
    { key: 'smtp',   icon: Send,        label: 'Set up Agency SMTP', hint: 'Send invoices, resets, nudges', to: '/admin/settings/integrations',  color: '#5B2D8E', required: true },
    { key: 'bank',   icon: CreditCard,  label: 'Add a bank account', hint: 'For Wise-style invoicing',     to: '/admin/settings/bank-accounts', color: '#3C8A5A', required: true },
    { key: 'zoom',   icon: Video,       label: 'Connect Zoom',       hint: 'One-click client meeting links', to: '/admin/settings/zoom',          color: '#2D7EE8', required: false },
    { key: 'r2',     icon: UploadCloud, label: 'Enable R2 uploads',  hint: 'Inline doc / video uploads',    to: '/admin/settings/uploads',       color: '#D9A441', required: false },
  ];

  const missing = steps.filter(s => !state[s.key]);
  const done = steps.length - missing.length;
  const pct = Math.round((done / steps.length) * 100);

  if (missing.length === 0) return null;

  return (
    <div
      className="card-base p-4 mb-5 animate-fade-in"
      style={{ background: 'linear-gradient(135deg, #FFF9E8 0%, #FFFFFF 60%)', borderLeft: '3px solid #D9A441' }}
      data-testid="setup-checklist"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-8 h-8 rounded-btn flex items-center justify-center flex-shrink-0" style={{ background: '#D9A44120' }}>
          <Rocket size={14} style={{ color: '#D9A441' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-black" style={{ color: 'var(--ink)' }}>Finish your setup</p>
            <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm" style={{ background: '#D9A44120', color: '#8A6B1A' }}>
              {done}/{steps.length} · {pct}%
            </span>
          </div>
          <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>
            A couple of integrations unlock the rest of Krris OS. Jump straight in:
          </p>
        </div>
        <button
          onClick={dismiss}
          className="p-1 rounded-btn opacity-60 hover:opacity-100 transition-opacity flex-shrink-0"
          style={{ color: 'var(--muted)' }}
          aria-label="Dismiss setup checklist"
          data-testid="setup-checklist-dismiss"
        >
          <X size={14} />
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {missing.map(s => {
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              onClick={() => navigate(s.to)}
              className="group flex items-center gap-2.5 text-left px-2.5 py-2 rounded-btn transition-all hover:translate-x-0.5"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
              data-testid={`setup-step-${s.key}`}
            >
              <div className="w-7 h-7 rounded-btn flex items-center justify-center flex-shrink-0" style={{ background: `${s.color}18`, color: s.color }}>
                <Icon size={13} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-xs font-bold truncate" style={{ color: 'var(--ink)' }}>{s.label}</p>
                  {!s.required && <span className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: 'var(--muted)' }}>opt</span>}
                </div>
                <p className="text-[10px] truncate" style={{ color: 'var(--muted)' }}>{s.hint}</p>
              </div>
              <ArrowRight size={12} className="flex-shrink-0 opacity-40 group-hover:opacity-100 transition-opacity" style={{ color: s.color }} />
            </button>
          );
        })}

        {/* Show the already-done ones as tiny badges so user sees progress */}
        {done > 0 && steps.filter(s => state[s.key]).map(s => (
          <div
            key={s.key}
            className="flex items-center gap-2 px-2.5 py-2 rounded-btn opacity-50"
            style={{ background: 'var(--bg)', border: '1px dashed var(--border)' }}
            data-testid={`setup-step-done-${s.key}`}
          >
            <div className="w-7 h-7 rounded-btn flex items-center justify-center flex-shrink-0" style={{ background: '#3C8A5A18', color: '#3C8A5A' }}>
              <Check size={13} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate line-through" style={{ color: 'var(--muted)' }}>{s.label}</p>
              <p className="text-[10px] truncate" style={{ color: 'var(--muted)' }}>Configured</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
